import React from 'react';

import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
} from '@mui/material/';
import { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { IconButton, Typography } from '../../atoms';
import { ArrowLeft as ArrowLeftIcon, X as XIcon } from '../../icons';

export interface DialogProps extends MuiDialogProps {
  actions?: JSX.Element[] | JSX.Element;
  children: React.ReactNode;
  dialogName?: string;
  endIcon?: JSX.Element;
  onTitleClick?: React.MouseEventHandler<HTMLButtonElement>;
  startIcon?: JSX.Element;
  title?: string;
  useNonFixedCtas?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  titleContainer: {
    minHeight: '56px',
    alignItems: 'center',
  },
  defaultTitle: {
    [theme.breakpoints.up('sm')]: {
      '& .iconLeft': {
        display: 'none',
      },

      '& .iconRight': {
        display: 'block',
      },
    },
  },
  dialogTitleText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.pxToRem(16),

    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  nonBorderedActions: {
    borderTop: 'none',
  },
  nonflexDialogContent: {
    flex: 'none',
  },
}));

export function Dialog(props: DialogProps): JSX.Element {
  const {
    actions = null,
    children,
    dialogName,
    endIcon = null,
    onTitleClick = null,
    startIcon = null,
    title = '',
    useNonFixedCtas = false,
    ...rest
  } = props;

  const classes = useStyles();

  const dialogStyles = clsx(
    'fd-dialog',
    dialogName && `fd-dialog__${dialogName.toLowerCase()}`,
  );

  const dialogTitleStyles = clsx(
    classes.titleContainer,
    onTitleClick && classes.defaultTitle,
  );

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const contentStyles = clsx(useNonFixedCtas && classes.nonflexDialogContent);
  const actionStyles = clsx(useNonFixedCtas && classes.nonBorderedActions);
  const defaultDialogTitle = (
    <>
      <IconButton
        className="iconLeft"
        color="secondary"
        disableRipple
        onClick={onTitleClick}
      >
        <ArrowLeftIcon />
      </IconButton>

      <Typography variant="body1">{title}</Typography>

      <IconButton
        className="iconRight"
        color="secondary"
        disableRipple
        onClick={onTitleClick}
      >
        <XIcon />
      </IconButton>
    </>
  );

  return (
    <MuiDialog
      aria-label={title}
      fullScreen={isSmallScreen}
      fullWidth={isSmallScreen}
      onClose={onTitleClick}
      PaperProps={{
        className: dialogStyles,
        style: {
          // @ts-ignore CSS variable required for downstream customisation
          '--fdPrimaryColor': theme.palette.primary.main,
        },
      }}
      {...rest}
    >
      <MuiDialogTitle className={dialogTitleStyles}>
        {onTitleClick ? (
          defaultDialogTitle
        ) : (
          <>
            {startIcon}
            <Typography className={classes.dialogTitleText} variant="body1">
              {title}
            </Typography>
            {endIcon}
          </>
        )}
      </MuiDialogTitle>

      <MuiDialogContent className={contentStyles} data-fd="dialog-content">
        {children}
      </MuiDialogContent>

      {actions && (
        <MuiDialogActions className={actionStyles} data-fd="dialog-actions">
          {actions}
        </MuiDialogActions>
      )}
    </MuiDialog>
  );
}
